import { GridRowParams } from '@mui/x-data-grid-premium'
import {
  GridColDef,
  GridRowSelectionModel,
  GridSortModel,
} from '@mui/x-data-grid-pro'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  renderActiveUntilCell,
  renderAvailableCreditCell,
  renderCardCreatorCell,
  renderInvoiceAmountCell,
  renderInvoiceDateCell,
  renderInvoiceNumberCell,
  renderSentDateCell,
  renderStatusCell,
  renderVendorEmailCell,
  renderVendorNameCell,
} from 'src/components/BillPayDataGrid/Columns/BillPayDataGridColumns'
import { chevronColumnDef } from 'src/components/DataGrid/cells/ChevronCell'
import DataGrid, { DataGridRowHeight } from 'src/components/DataGrid/DataGrid'
import { CheckboxProps } from 'src/components/VirtualCardsBox/VirtualCardsBox'
import { VirtualCardsFiltersBody } from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import { translateSingleString } from 'src/localization/Trns'
import { VirtualCardListModel } from 'src/models/virtualCard.models'
import { VirtualCardPermissions } from 'src/models/virtualCardPermissions.models'
import { connectedPush } from 'src/navigation/Lnk'
import { billPayDetailUrl } from 'src/navigation/urls'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { InfiniteScrollProps } from 'src/types'

export interface BillPayDataGridRow {
  id: string
  virtualCard: VirtualCardListModel
  index: number
}
type VirtualCardsDataGridProps = {
  virtualCards: VirtualCardListModel[]
  virtualCardsPermissions?: Array<{
    permissions: VirtualCardPermissions[]
    virtualCardId: string
  }>
  infiniteScrollProps?: InfiniteScrollProps<VirtualCardsFiltersBody>
  filters?: VirtualCardsFiltersBody
  checkboxProps?: CheckboxProps
  clientHeight?: number
  sortableProps?: {
    setSort: (field: string) => void
    sortBy?: string
    sortDescending?: boolean
  }
}

const BillPayDataGrid: React.FC<VirtualCardsDataGridProps> = (
  props,
): React.ReactElement => {
  // const { analytics } = useAnalyticsContext()
  const locale = useSelector(selectNullSafeLocaleFromState)

  const [sortModel, setSortModel] = React.useState<GridSortModel>()

  const dispatch = useDispatch()

  const rows: BillPayDataGridRow[] = props.virtualCards.map(
    (virtualCard, i) => {
      return {
        id: virtualCard?.id,
        virtualCard: virtualCard,
        index: i,
      }
    },
  )

  const columns: Array<GridColDef<BillPayDataGridRow>> = [
    {
      field: 'vendor',
      headerName: translateSingleString(locale, 'Vendor'),
      sortable: false,
      minWidth: 150,
      renderCell: renderVendorNameCell,
    },
    {
      field: 'vendorEmail',
      headerName: translateSingleString(locale, 'Vendor Email'),
      sortable: false,
      minWidth: 150,
      renderCell: renderVendorEmailCell,
    },
    // Card Creator
    {
      field: 'cardCreator',
      headerName: translateSingleString(locale, 'Card Creator'),
      sortable: false,
      minWidth: 150,
      renderCell: renderCardCreatorCell,
    },
    // Sent Date
    {
      field: 'sentDate',
      headerName: translateSingleString(locale, 'Sent Date'),
      sortable: false,
      minWidth: 150,
      renderCell: renderSentDateCell,
    },
    // Invoice Date
    {
      field: 'invoiceDate',
      headerName: translateSingleString(locale, 'Invoice Date'),
      sortable: false,
      minWidth: 150,
      renderCell: renderInvoiceDateCell,
    },
    // Invoice Number
    {
      field: 'invoiceNumber',
      headerName: translateSingleString(locale, 'Invoice Number'),
      sortable: false,
      minWidth: 150,
      renderCell: renderInvoiceNumberCell,
    },
    // Invoice Amount
    {
      field: 'invoiceAmount',
      headerName: translateSingleString(locale, 'Invoice Amount'),
      sortable: false,
      minWidth: 150,
      renderCell: renderInvoiceAmountCell,
    },
    // Available Credit
    {
      field: 'availableCredit',
      headerName: translateSingleString(locale, 'Available Credit'),
      sortable: false,
      minWidth: 150,
      renderCell: renderAvailableCreditCell,
    },
    // Active Until
    {
      field: 'activeUntil',
      headerName: translateSingleString(locale, 'Active Until'),
      sortable: false,
      minWidth: 150,
      renderCell: renderActiveUntilCell,
    },
    // Status
    {
      field: 'status',
      headerName: translateSingleString(locale, 'Status'),
      sortable: false,
      minWidth: 150,
      renderCell: renderStatusCell,
    },
    chevronColumnDef,
  ]

  return (
    <DataGrid
      rowHeight={DataGridRowHeight.Default}
      height={props.clientHeight}
      rows={rows}
      columns={columns}
      infiniteScrollProps={props.infiniteScrollProps}
      filtersBody={props.filters || {}}
      sortModel={sortModel}
      onSortModelChange={(newSortModel): void => setSortModel(newSortModel)}
      disableColumnFilter
      checkboxSelection={props.checkboxProps !== undefined}
      onRowSelectionModelChange={(selection: GridRowSelectionModel): void => {
        props.checkboxProps?.handleDataGridVirtualCardCheckboxClick(selection)
      }}
      isRowSelectable={(params: GridRowParams): boolean => {
        const virtualCard = params.row.virtualCard
        return !!props.checkboxProps?.canSelectVirtualCard(virtualCard)
      }}
      onRowClick={(row): void => {
        const virtualCard = row.row.virtualCard
        dispatch(
          connectedPush({
            pathname: billPayDetailUrl(virtualCard?.id),
            search: '',
            hash: '',
            state: {},
          }),
        )
      }}
    />
  )
}

export default BillPayDataGrid
